import { InputLabel, MenuItem } from '@mui/material';
import { DateTime } from 'luxon';
import { memo } from 'react';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { TypeResidence } from 'shell/models/type-payment';
import { transformerModalityPayment } from 'shell/utils/transformer-modality-payment';
import { ItemVM } from 'shell/view-models/item';
import { ModalityPaymentVM } from 'shell/view-models/modality-payment';
import { Operation } from 'shell/view-models/operation';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';
import { FlagUseModeCode } from 'shell/constants/use-code-mode';

interface ModalityPaymentProps {
  disabled?: boolean;
  value?: ModalityPaymentVM | null;
  onChange: (element: ModalityPaymentVM | undefined) => void;
  error?: boolean;
  helperText?: string;
  flagUseModeCode?: string | null;
  required?: boolean;
  hasParams?: boolean;
  label?: string;
  id?: string;
  useModeCode?: string | null;
  filterElements?: (elements: Array<ModalityPaymentVM>) => Array<ModalityPaymentVM>;
}

const ModalityPaymentListComponent = ({
  id = 'modality-payment',
  disabled,
  onChange,
  value,
  error,
  helperText,
  flagUseModeCode = FlagUseModeCode.Certificazione,
  useModeCode,
  label = 'Modalità di rilascio',
  required = false,
  filterElements,
  hasParams = true,
}: ModalityPaymentProps): React.ReactElement => {
  const [modalityPayment] = useHttpFetch<HttpResponseList<TypeResidence>, Array<ModalityPaymentVM>>(
    `${__CERTIFICATION_API_URL__}/certificazione/modalita-rilascio`,
    {
      onBootstrap: true,
      initialData: [],
      options: {
        params: {},
        paramsSerializer: () =>
          hasParams
            ? `filters=today:${DateTime.now().toFormat('yyyy-MM-dd')}:${Operation.EQUALS}${
                flagUseModeCode ? `&filters=flagCodiceUsoModalita:${flagUseModeCode}` : ''
              }${
                useModeCode ? `&filters=codiceUsoModalita:${useModeCode}:${Operation.EQUALS}` : ''
              }`
            : '',
      },
      transformer: (response) => {
        let modalityPayments = transformerModalityPayment(response);

        if (filterElements) {
          modalityPayments = filterElements(modalityPayments);
        }

        return modalityPayments;
      },
    }
  );

  const list = (modalityPayment.data || []) as Array<ModalityPaymentVM>;

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} disabled={disabled} id={`label-${id}`}>
        {required ? `${label} *` : label}
      </InputLabel>
      <PmiSelect
        labelId={`label-${id}`}
        id={id}
        name={id}
        value={value?.id?.toString() || ''}
        label={required ? `${label} *` : label}
        onChange={(event) => {
          const selectedValue = event.target.value;
          const element = list.find((listItem) => listItem.id === selectedValue);
          onChange(element);
        }}
        disabled={disabled}
        error={error}
        helperText={helperText}
      >
        <MenuItem value="">
          <em>Nessuno</em>
        </MenuItem>
        {list.map((modality: ItemVM) => (
          <MenuItem key={modality.id} value={modality.id}>
            {`${modality.id} - ${modality.value}`}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ModalityPaymentListComponent.displayName = 'List modality-payment anpr component';

const ModalityPaymentList = memo(ModalityPaymentListComponent);

export default ModalityPaymentList as typeof ModalityPaymentListComponent;
